import { useQuery } from '@apollo/client/react'
import { RoleEnum, TrainerCapability } from '@zeus/index'
import { Pencil } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import AttendanceButton from '~/components/AttendanceButton'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { GQL_BACK_OFFICE_HOME } from '~/gql/home/home'
import { GQL_SESSION, GQL_SESSION_RESULTS } from '~/gql/sessions/sessions'
import { GQL_SURVEY_RESULTS } from '~/gql/surveys/surveys'
import { useAuth } from '~/helpers/store/auth'
import { tourAnchor } from '~/helpers/tour/anchors'
import { attendanceStatementPath } from '~/hooks/use-attendance-download'
import { useCan } from '~/hooks/use-can'
import { useReportDownload } from '~/hooks/use-report-download'
import SessionModal from '~/pages/backoffice/admin/sessions/components/SessionModal'
import { useSessionFormOptions } from '~/pages/backoffice/admin/sessions/use-session-form-options'
import DropoutAction from './DropoutAction'
import SessionDetailView from './SessionDetailView'
import SessionResultsPlaceholder from './SessionResultsPlaceholder'

/**
 * Fiche d'une session (#243), ouverte depuis la liste. Les résultats suivent le
 * droit de voir les statistiques, la modification celui de gérer les parcours :
 * la modale de la liste reste l'unique point d'édition.
 */
function SessionDetailPage() {
  const { t } = useTranslation()
  const { sessionId = '' } = useParams()
  const canManage = useCan(TrainerCapability.SESSIONS_MANAGE)
  const canSeeResults = useCan(TrainerCapability.STATISTICS_VIEW)
  const isAdmin = useAuth().payload?.role === RoleEnum.Admin
  const [editing, setEditing] = useState(false)

  const { data, loading, refetch } = useQuery(GQL_SESSION, { variables: { id: sessionId }, fetchPolicy: 'cache-and-network' })
  const { data: resultsData, error: resultsError, refetch: refetchResults } = useQuery(GQL_SESSION_RESULTS, {
    variables: { sessionId },
    skip: !canSeeResults,
    fetchPolicy: 'cache-and-network',
  })
  const { data: surveyData } = useQuery(GQL_SURVEY_RESULTS, { variables: { sessionId }, skip: !canSeeResults, fetchPolicy: 'cache-and-network' })
  const { data: homeData } = useQuery(GQL_BACK_OFFICE_HOME, { fetchPolicy: 'cache-first' })
  const { readyModules, users, groups, certificateTemplates } = useSessionFormOptions(canManage)
  const report = useReportDownload(`/statistics/report?sessionId=${sessionId}`, 'rapport.pdf')
  const attendance = useReportDownload(attendanceStatementPath(sessionId), 'releves-assiduite.pdf')

  const session = data?.session
  if (loading && !session) return <div className="flex justify-center p-16"><Spinner className="size-6 text-muted-foreground" /></div>

  if (!session) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border p-12 text-center">
        <p className="font-medium">{t('sessionDetail.notFound')}</p>
        <Button asChild variant="outline"><Link to="/back-office/sessions">{t('sessionDetail.back')}</Link></Button>
      </div>
    )
  }

  return (
    <>
      <SessionDetailView
        session={session}
        results={canSeeResults ? resultsData?.sessionResults : undefined}
        resultsPlaceholder={canSeeResults && <SessionResultsPlaceholder failed={!!resultsError} onRetry={() => void refetchResults()} />}
        timeZone={homeData?.backOfficeHome?.timezone}
        templateName={certificateTemplates.find(template => template.id === session.certificateTemplateId)?.name}
        onDownloadReport={() => void report.download()}
        downloadingReport={report.downloading}
        survey={surveyData?.surveyResults}
        attendance={isAdmin && (
          <AttendanceButton onClick={() => void attendance.download()} loading={attendance.downloading} {...tourAnchor('sessionDetail.attendance')}>
            {t('attendance.batch')}
          </AttendanceButton>
        )}
        participantAction={canManage
          ? participant => <DropoutAction sessionId={sessionId} participant={participant} onChanged={() => void refetchResults()} />
          : undefined}
        headerActions={canManage && (
          <Button variant="outline" onClick={() => setEditing(true)} {...tourAnchor('sessionDetail.edit')}>
            <Pencil className="mr-2 size-4" />
            {t('sessionDetail.edit')}
          </Button>
        )}
      />
      {editing && (
        <SessionModal
          open={editing}
          onCancel={() => setEditing(false)}
          onSuccess={() => Promise.all([refetch(), canSeeResults && refetchResults()])}
          modules={readyModules}
          users={users}
          groups={groups}
          certificateTemplates={certificateTemplates}
          session={session}
          tagSuggestions={session.tags.map(tag => tag.name)}
        />
      )}
    </>
  )
}

export default SessionDetailPage
